import { GetServerSidePropsContext } from 'next'
import { useEffect } from 'react'
import dynamic from 'next/dynamic'

import { StaticDataProvider } from '@mc/static-data-react'
import type { GrowthCategory, Product } from '@mc/api'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { TRACK_PROPS } from '@mc/track-event'
import {
  useTrackPen5108Experiment,
  Pen5108PromoBannerWrapper,
  Pen5108ForBusinessWrapper,
} from '@mc/promos'

import { trackProductViewed } from 'libs/events'
import {
  getDefaultProduct,
  getGuestPassByReferralToken,
  getMarketingRows,
  getV2Categories,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { MarketingDataType } from 'types'

import { GoogleOneTap } from 'experiments/pen-3951_v2_google_email_capture/components/GoogleOneTap'

import { ForBusiness } from './components/ForBusiness'
import { Hero } from './components/Hero'
import { Meta } from './components/Meta'

const BelowTheFold = dynamic(() => import('./components/BelowTheFold'), {
  ssr: false,
})

interface GiftProps {
  annualPass?: Product
  categories?: GrowthCategory[]
  marketingRows?: MarketingDataType[]
}

const Gift = ({ annualPass, categories, marketingRows }: GiftProps) => {
  useEffect(() => {
    if (annualPass) {
      trackProductViewed(annualPass)
    }
  }, [annualPass])

  useTrackPen5108Experiment()

  return (
    <StaticDataProvider value={{ annualPass, categories, marketingRows }}>
      <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-gift' }}>
        <Pen5108PromoBannerWrapper />
        <Meta />
        <Hero />
        <Pen5108ForBusinessWrapper control={<ForBusiness />} />
        <GoogleOneTap />
        <BelowTheFold />
      </TrackingParamsProvider>
    </StaticDataProvider>
  )
}

Gift.pageSettings = {
  rumLabel: 'Gift',
  analyticsLabel: 'Gifting Landing',
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    req: {
      cookies: { referral_token: referralToken },
    },
  } = ctx

  return getPageData({
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      guestPass: getGuestPassByReferralToken(ctx, { referralToken }),
      marketingRows: getMarketingRows(ctx),
      categories: getV2Categories(ctx),
    },
  })
}

export default Gift
